import { Fragment, HTMLProps, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, getSortedRowModel, SortingState, useReactTable, ExpandedState } from "@tanstack/react-table";

import { CategoryWithParts, ILeadPartsTableGlobalFilters, IPart, PhysicalRouting } from "../../../../models/Part";
import { PartCategoryConflictMap } from "../../../../models/PartCategorySubPart";
import { buildBestMatrixPriceByCategoryId, IBestPriceOption } from "./bestMatrixPrice";
import { ChevronRightIcon, EllipsisVerticalIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import classNames from "../../../../utils/classNames";
import LeadPartsCategoriesTableSubRow from "./LeadPartsCategoriesTableSubRow";
import { DateTime } from "luxon";
import LoadingPulse from "../../../shared/LoadingPulse";
import { LastSoldPriceButton, SmartPriceButton, PartProPriceButton } from "../../../shared/PriceButtons";
import formatDateTime from "../../../../utils/formatDateTime";
import EbayLinkButton from "../../../shared/Inputs/EbayLinkButton";
import { IVehicle } from "../../../../models/Vehicle";
import useSingleSetting from "../../../../data/useSingleSetting";
import RsPriceButton from "../../../shared/PriceButtons/RsPriceButton";
import PartDiagramModal, { DiagramMode } from "../../../GtMotiveWidgets/PartDiagramModal";
import PriceFormat from "../../../../utils/priceFormat";
import { MAX_PART_PRICE } from "../../../../utils/priceLimits";
import useVehiclePricingMatrix from "../../../../data/useVehiclePricingMatrix";
import Modal from "../../../../layouts/Modal";
import { XMarkIcon as XMarkSolidIcon } from "@heroicons/react/24/solid";
import useFlowTypeAccounts from "../../../../data/useFlowTypeAccounts";
import { CheckFeatureFlag } from "../../../../hooks/useFeatureFlag";
import useDamageAssessment from "../../../../data/useDamageAssessment";
import DamageImagesModal, { IDamageViewerImage } from "../../../jobs/DamageImagesModal";

const colHelper = createColumnHelper<CategoryWithParts>();
const accountTypeLabelMap: Record<number, string> = {
  5: "Reuse",
  6: "Remanufacturing",
  7: "Reintegration",
  8: "Recycling",
};
const accountTypePillClassMap: Record<number, string> = {
  5: "border-emerald-200 bg-emerald-50 text-emerald-700",
  6: "border-orange-200 bg-orange-50 text-orange-700",
  7: "border-blue-200 bg-blue-50 text-blue-700",
  8: "border-purple-200 bg-purple-50 text-purple-700",
};

interface IAccountSelectionPayload {
  accountId: string | null;
  accountName: string | null;
  accountType: number | null;
  physicalRoutingDefault?: PhysicalRouting | null;
}

const physicalRoutingOptions: { value: PhysicalRouting; label: string }[] = [
  { value: PhysicalRouting.None, label: "None" },
  { value: PhysicalRouting.Bins, label: "Bins" },
  { value: PhysicalRouting.Staging, label: "Staging" },
  { value: PhysicalRouting.Pickup, label: "Pickup" },
];

// What the auto-select effect last applied to a row, so we can re-evaluate
// (e.g. upgrade to RS once RS prices load) without clobbering manual edits.
type AutoSelectIntent =
  | { kind: "rs"; price: number }
  | { kind: "matrix"; price: number; accountId: string }
  | { kind: "recycling"; accountId: string; price: number };

function IndeterminateCheckbox({
  indeterminate,
  className = "",
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (typeof indeterminate === "boolean") {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate]);

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + "p-3 cursor-pointer"}
      {...rest}
    />
  );
}


function EditablePriceCell({
  rowId,
  value,
  handleSetTableData
}: {
  rowId: string;
  value: number;
  handleSetTableData: (
    rowId: string,
    price: number | undefined,
    note?: string | undefined,
    isCore?: boolean | undefined,
    accountSelection?: IAccountSelectionPayload | undefined,
    clearSelectedAccount?: boolean
  ) => void;
}) {
  const [localPriceValue, setLocalPriceValue] = useState<number>(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const commitTimerRef = useRef<number | undefined>(undefined);

  // Whenever the parent value changes (e.g. clicking the Suggested Price button),
  // update localValue so the input shows the latest.
  useEffect(() => {
    // Avoid clobbering the in-progress value while this field has focus.
    if (document.activeElement === inputRef.current) return;
    setLocalPriceValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (typeof commitTimerRef.current !== "undefined") {
        window.clearTimeout(commitTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="relative w-24">
      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">£</span>
      <input
        ref={inputRef}
        type="number"
        value={localPriceValue}
        min={0}
        max={MAX_PART_PRICE}
        onChange={(e) => {
          // If user clears the field, treat as 0
          const raw = e.target.value;
          const parsed = raw === "" ? 0 : parseFloat(raw);
          const safe = Number.isNaN(parsed) ? 0 : Math.min(parsed, MAX_PART_PRICE);
          setLocalPriceValue(safe);
        }}
        onBlur={() => {
          // Defer commit so focus transition to the next field can complete first.
          if (localPriceValue !== value) {
            const valueToCommit = localPriceValue;
            if (typeof commitTimerRef.current !== "undefined") {
              window.clearTimeout(commitTimerRef.current);
            }
            commitTimerRef.current = window.setTimeout(() => {
              handleSetTableData(rowId, valueToCommit);
            }, 0);
          }
        }}
        className="border p-1 pl-5 w-full"
      />
    </div>
  );
}

function EditableNoteCell({ rowId, value, handleSetTableData }: { rowId: string; value: string; handleSetTableData: (rowId: string, price: number | undefined, note?: string | undefined) => void; }) {
  const [localNoteValue, setLocalNoteValue] = useState<string>(value);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const commitTimerRef = useRef<number | undefined>(undefined);

  // Keep local state in sync if parent value changes (e.g., external updates)
  useEffect(() => {
    // Avoid clobbering the in-progress value while this field has focus.
    if (document.activeElement === textAreaRef.current) return;
    setLocalNoteValue(value ?? "");
  }, [value]);

  useEffect(() => {
    return () => {
      if (typeof commitTimerRef.current !== "undefined") {
        window.clearTimeout(commitTimerRef.current);
      }
    };
  }, []);

  return (
    <textarea
      ref={textAreaRef}
      value={localNoteValue}
      rows={1}
      onChange={(e) => {
        setLocalNoteValue(e.target.value);
      }}
      onBlur={() => {
        // Defer commit so focus transition to the next field can complete first.
        if (localNoteValue !== value) {
          const valueToCommit = localNoteValue;
          if (typeof commitTimerRef.current !== "undefined") {
            window.clearTimeout(commitTimerRef.current);
          }
          commitTimerRef.current = window.setTimeout(() => {
            handleSetTableData(rowId, undefined, valueToCommit);
          }, 0);
        }
      }}
      className="border p-1 text-sm w-full"
    />
  );
}

export interface LeadPartsCategoriesTableProps {
  categories: CategoryWithParts[];
  selectedCategories?: CategoryWithParts[];
  setSelectedCategories: React.Dispatch<React.SetStateAction<CategoryWithParts[]>>;
  partProUnlocked: boolean;
  partProLoading: boolean;
  vehicle: IVehicle;
  filter?: (cat: CategoryWithParts) => boolean;
  leadPartsTableFilters: ILeadPartsTableGlobalFilters;
  showBestPrice?: boolean;
  autoSelect?: boolean;
  /**
   * Global (cross-table) main/sub-part clashes, keyed by category id. Computed in CreateJobs
   * because a main and its sub-parts routinely sit in different tables.
   */
  categoryConflicts?: PartCategoryConflictMap;
  /**
   * Sub-part ids the TME auto-select must skip. Intentionally has no default: `undefined`
   * means the link data hasn't loaded yet and the auto-select effect must hold off.
   */
  autoSelectSuppressedCategoryIds?: Set<string>;
}

export default function LeadPartsCategoriesTable({
  categories,
  selectedCategories,
  setSelectedCategories,
  partProUnlocked,
  partProLoading,
  vehicle,
  filter,
  leadPartsTableFilters,
  showBestPrice = false,
  autoSelect = false,
  categoryConflicts,
  autoSelectSuppressedCategoryIds,
}: LeadPartsCategoriesTableProps) {
  const hasTmeFeature = CheckFeatureFlag("TME");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [tableData, setTableData] = useState<CategoryWithParts[]>(categories);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const { singleSetting } = useSingleSetting("Rs:ApiKey");
  const [hasRsKey, setHasRsKey] = useState(false);
  const [diagramOpen, setDiagramOpen] = useState(false);
  const [diagramMode, setDiagramMode] = useState<DiagramMode>({ type: "findAll" });
  const [diagramCategory, setDiagramCategory] = useState<CategoryWithParts["category"] | null>(null);
  const [priceOptionsModalOpen, setPriceOptionsModalOpen] = useState(false);
  const [selectedPriceOptions, setSelectedPriceOptions] = useState<{
    categoryId: string;
    categoryName: string;
    bestOption: IBestPriceOption | null;
    otherOptions: IBestPriceOption[];
    rsPrice: number | null;
  } | null>(null);
  const setSelectedCategoriesRef = useRef(setSelectedCategories);
  const tableDataRef = useRef(tableData);
  // Assigned during render, not in an effect, because the Name-column cell renderer reads it
  // in the same pass. Going through a ref keeps `categoryConflicts` out of the baseCols deps
  // below, so a new conflict map (one per selection change) doesn't rebuild every column.
  const categoryConflictsRef = useRef(categoryConflicts);
  categoryConflictsRef.current = categoryConflicts;

  // Damage AI (Tractable) results for this vehicle — self-gated on the "Damage AI" flag.
  const { assessment: damageAssessment } = useDamageAssessment(vehicle?.id);
  const [damageViewer, setDamageViewer] = useState<{ categoryName: string; tractableCode: string } | null>(null);

  const damagedByCategoryId = useMemo(() => {
    const map = new Map<string, string>();
    damageAssessment.data?.data?.damagedCategories?.forEach((c) => map.set(c.partCategoryId, c.tractableCode));
    return map;
  }, [damageAssessment.data]);

  // Same ref trick as categoryConflictsRef: the Name-column cell reads it without the
  // damage map becoming a baseCols dependency.
  const damagedByCategoryIdRef = useRef(damagedByCategoryId);
  damagedByCategoryIdRef.current = damagedByCategoryId;
  const setDamageViewerRef = useRef(setDamageViewer);
  setDamageViewerRef.current = setDamageViewer;

  const damageViewerImages = useMemo<IDamageViewerImage[]>(() => {
    const data = damageAssessment.data?.data;
    if (!data || !damageViewer) return [];

    const result = data.partResults.find(
      (r) => r.tractableCode.toLowerCase() === damageViewer.tractableCode.toLowerCase()
    );
    const urls = Array.from(new Set((result?.damageInstances ?? []).flatMap((i) => i.annotatedImageUrls)));
    // Fall back to every annotated image when the per-instance linkage produced nothing.
    const finalUrls = urls.length > 0 ? urls : data.annotatedImages.map((a) => a.cdnUrl);

    return finalUrls.map((url, index) => ({ id: url, url, fileName: `damage-${index + 1}` }));
  }, [damageAssessment.data, damageViewer]);
  const { vehiclePricingMatrix } = useVehiclePricingMatrix(vehicle?.id, "LeadPartsCategoriesTable");
  const { flowTypeAccounts } = useFlowTypeAccounts("LeadPartsCategoriesTable");

  useEffect(() => {
    setSelectedCategoriesRef.current = setSelectedCategories;
  }, [setSelectedCategories]);

  useEffect(() => {
    tableDataRef.current = tableData;
  }, [tableData]);

  const filteredTableData = useMemo(() => {
    return filter ? tableData.filter(filter) : tableData;
  }, [tableData, filter]);

  const rowSelection = useMemo(() => {
    const sel: Record<string, boolean> = {};
    selectedCategories?.forEach((cat) => {
      sel[cat.category.id] = true;
    });
    return sel;
  }, [selectedCategories]);

  const handleSetTableData = useCallback((
    rowId: string,
    newPrice: number | undefined,
    newNote: string | undefined = undefined,
    isCore: boolean | undefined = undefined, // Is refurbished / Rs
    accountSelection: IAccountSelectionPayload | undefined = undefined,
    clearSelectedAccount: boolean = false,
    physicalRouting: PhysicalRouting | undefined = undefined
  ) => {
    const currentTableData = tableDataRef.current;
    const existingRow = currentTableData.find((item) => item.category.id === rowId);
    if (!existingRow) return;

    const hasNumericPrice = typeof newPrice === "number" && !Number.isNaN(newPrice);
    const numericPrice = hasNumericPrice ? newPrice : undefined;
    const shouldUpdateNote = typeof newNote !== "undefined";
    const shouldUpdateCore = typeof isCore === "boolean";
    const shouldUpdateAccount = typeof accountSelection !== "undefined" || clearSelectedAccount;
    const shouldUpdatePhysicalRouting = typeof physicalRouting !== "undefined";
    const shouldClearAccountForPriceReset =
      hasNumericPrice && numericPrice! <= 0 && typeof accountSelection === "undefined";

    const nextPrice = hasNumericPrice ? numericPrice : existingRow.price;
    const nextNote = shouldUpdateNote ? newNote : existingRow.note;
    const nextIsCore = shouldUpdateCore ? isCore : existingRow.isCore;
    const nextAccountId = shouldClearAccountForPriceReset || clearSelectedAccount
      ? null
      : shouldUpdateAccount
        ? accountSelection?.accountId ?? null
        : existingRow.accountId ?? null;
    const nextAccountName = shouldClearAccountForPriceReset || clearSelectedAccount
      ? null
      : shouldUpdateAccount
        ? accountSelection?.accountName ?? null
        : existingRow.accountName ?? null;
    const nextAccountType = shouldClearAccountForPriceReset || clearSelectedAccount
      ? null
      : shouldUpdateAccount
        ? accountSelection?.accountType ?? null
        : existingRow.accountType ?? null;
    // Routing follows the account: clearing the account resets it, selecting an
    // account defaults it to that account's PhysicalRoutingDefault, and an explicit
    // dropdown change overrides everything.
    const nextPhysicalRouting = shouldClearAccountForPriceReset || clearSelectedAccount
      ? PhysicalRouting.None
      : shouldUpdatePhysicalRouting
        ? physicalRouting
        : shouldUpdateAccount
          ? accountSelection?.physicalRoutingDefault ?? PhysicalRouting.None
          : existingRow.physicalRouting ?? PhysicalRouting.None;

    const updatedRow: CategoryWithParts = {
      ...existingRow,
      price: nextPrice,
      note: nextNote,
      isCore: nextIsCore,
      accountId: nextAccountId,
      accountName: nextAccountName,
      accountType: nextAccountType,
      physicalRouting: nextPhysicalRouting,
    };

    const nextSelectedUpdater = (prevSelectedForThisTable: CategoryWithParts[]) => {
      const selectedIndex = prevSelectedForThisTable.findIndex(
        (cat) => cat.category.id === rowId
      );
      const isCurrentlySelected = selectedIndex !== -1;

      if (hasNumericPrice) {
        // £0 usually means "deselect", but recycling is intentionally £0 with an account.
        if (numericPrice! <= 0 && !updatedRow.accountId) {
          if (!isCurrentlySelected) return prevSelectedForThisTable;
          return prevSelectedForThisTable.filter((cat) => cat.category.id !== rowId);
        }

        if (!isCurrentlySelected) {
          return [...prevSelectedForThisTable, updatedRow];
        }

        const nextSelected = [...prevSelectedForThisTable];
        nextSelected[selectedIndex] = updatedRow;
        return nextSelected;
      }

      if (shouldUpdateNote && isCurrentlySelected) {
        const nextSelected = [...prevSelectedForThisTable];
        nextSelected[selectedIndex] = updatedRow;
        return nextSelected;
      }

      if (shouldUpdateAccount || shouldUpdateCore || shouldUpdatePhysicalRouting) {
        if (!isCurrentlySelected) {
          return [...prevSelectedForThisTable, updatedRow];
        }

        const nextSelected = [...prevSelectedForThisTable];
        nextSelected[selectedIndex] = updatedRow;
        return nextSelected;
      }

      return prevSelectedForThisTable;
    };

    setSelectedCategoriesRef.current(nextSelectedUpdater);

    const didChange =
      nextPrice !== existingRow.price ||
      nextNote !== existingRow.note ||
      nextIsCore !== existingRow.isCore ||
      nextAccountId !== existingRow.accountId ||
      nextAccountName !== existingRow.accountName ||
      nextAccountType !== existingRow.accountType ||
      nextPhysicalRouting !== (existingRow.physicalRouting ?? PhysicalRouting.None);

    if (!didChange) return;

    setTableData((old) => old.map((item) => (
      item.category.id === rowId ? updatedRow : item
    )));
  }, []);

  const makeModelString = useMemo(() => {
    let string = "";

    if (vehicle?.dvlaYearOfManufacture) {
      string = vehicle?.dvlaYearOfManufacture + "+";
    }

    string += vehicle?.combinedMake;
    string += "+";
    string += vehicle?.modelRangeDescription;

    return string;
  }, [vehicle])

  // Shared with CreateJobs, which needs the same "does this category have a price?" judgement
  // globally to decide between a main category and its sub-parts.
  const bestMatrixPriceByCategoryId = useMemo(
    () => buildBestMatrixPriceByCategoryId(vehiclePricingMatrix.data?.data),
    [vehiclePricingMatrix.data?.data]
  );

  const firstRecyclingAccount = useMemo(() => {
    const recyclingAccount = flowTypeAccounts.data?.data?.find((account) => account.accountType === 8);
    if (!recyclingAccount) {
      return null;
    }

    return {
      accountId: recyclingAccount.id,
      accountName: recyclingAccount.name,
      accountType: recyclingAccount.accountType,
      physicalRoutingDefault: recyclingAccount.physicalRoutingDefault ?? PhysicalRouting.None,
    };
  }, [flowTypeAccounts.data?.data]);

  const handleOpenPriceOptions = useCallback((row: CategoryWithParts) => {
    const pricingData = bestMatrixPriceByCategoryId.get(row.category.id);

    setSelectedPriceOptions({
      categoryId: row.category.id,
      categoryName: row.category.name,
      bestOption: pricingData?.bestOption ?? null,
      otherOptions: pricingData ? pricingData.options.slice(1) : [],
      rsPrice: row.category.rsPrice ?? null,
    });
    setPriceOptionsModalOpen(true);
  }, [bestMatrixPriceByCategoryId]);

  const handleSelectModalPrice = useCallback((option: IBestPriceOption) => {
    if (!selectedPriceOptions) {
      return;
    }

    handleSetTableData(selectedPriceOptions.categoryId, option.price, undefined, false, {
      accountId: option.accountId,
      accountName: option.accountName,
      accountType: option.accountType,
      physicalRoutingDefault: option.physicalRoutingDefault,
    });
    setPriceOptionsModalOpen(false);
  }, [handleSetTableData, selectedPriceOptions]);

  const handleSelectModalRs = useCallback(() => {
    if (!selectedPriceOptions || selectedPriceOptions.rsPrice == null) {
      return;
    }

    // Mirror the Flow-cell RS button: select as core/RS and clear any account.
    handleSetTableData(selectedPriceOptions.categoryId, selectedPriceOptions.rsPrice, undefined, true, undefined, true);
    setPriceOptionsModalOpen(false);
  }, [handleSetTableData, selectedPriceOptions]);

  const handleSelectModalRecycling = useCallback(() => {
    if (!selectedPriceOptions || !firstRecyclingAccount) {
      return;
    }

    handleSetTableData(selectedPriceOptions.categoryId, 0, undefined, false, {
      accountId: firstRecyclingAccount.accountId,
      accountName: firstRecyclingAccount.accountName,
      accountType: firstRecyclingAccount.accountType,
      physicalRoutingDefault: firstRecyclingAccount.physicalRoutingDefault,
    });
    setPriceOptionsModalOpen(false);
  }, [handleSetTableData, selectedPriceOptions, firstRecyclingAccount]);

  // When the TME feature is enabled, automatically select the best price for
  // every category: RS if it beats the matrix, otherwise the best matrix price,
  // falling back to the recycling account when no price exists. Only applies to
  // assembly tables (autoSelect) — non-assembly/manual parts are left untouched.
  //
  // RS prices arrive asynchronously (after the RS lookup completes), so this
  // can't be a one-shot run. We remember what we last auto-applied to each row
  // and re-evaluate as data loads, upgrading to RS once it's available — but
  // only while the row still matches what we applied (i.e. the user hasn't
  // manually changed it).
  const autoSelectedVehicleRef = useRef<string | null>(null);
  const autoSelectedIntentsRef = useRef<Record<string, AutoSelectIntent>>({});
  useEffect(() => {
    if (!showBestPrice || !autoSelect) return;
    if (vehiclePricingMatrix.isLoading || flowTypeAccounts.isLoading) return;
    if (!tableData.length) return;
    // Sub-part link data hasn't resolved yet. Hold off entirely rather than risk selecting a
    // sub-part we'd have suppressed a moment later - we never auto-deselect, so there'd be no
    // way back.
    if (!autoSelectSuppressedCategoryIds) return;

    // Forget remembered intents when we switch vehicles.
    if (autoSelectedVehicleRef.current !== (vehicle?.id ?? null)) {
      autoSelectedVehicleRef.current = vehicle?.id ?? null;
      autoSelectedIntentsRef.current = {};
    }

    tableData.forEach((row) => {
      // Removed parts (kept visible only because they belong to an assembly)
      // must never be auto-selected.
      if (row.isRemoved) return;

      const rowId = row.category.id;

      // Never auto-select a sub-part of a main category that is itself being auto-selected -
      // taking the main takes its sub-parts with it. Must stay above the intent bookkeeping
      // below: a suppressed row then never records an intent, so it can't be "upgraded" into
      // a selection when an async RS price lands and this effect re-runs.
      if (autoSelectSuppressedCategoryIds.has(rowId)) return;

      const bestOption = bestMatrixPriceByCategoryId.get(rowId)?.bestOption;
      const rsPrice = row.category.rsPrice ?? null;
      const rsIsBest = rsPrice != null && rsPrice > (bestOption?.price ?? 0);

      // Work out what we'd ideally auto-select for this row right now.
      let desired: AutoSelectIntent | null = null;
      if (rsIsBest) {
        desired = { kind: "rs", price: rsPrice };
      } else if (bestOption) {
        desired = { kind: "matrix", price: bestOption.price, accountId: bestOption.accountId };
      } else if (firstRecyclingAccount) {
        desired = { kind: "recycling", accountId: firstRecyclingAccount.accountId, price: 0 };
      }
      if (!desired) return;

      const prevIntent = autoSelectedIntentsRef.current[rowId];

      if (!prevIntent) {
        // Never auto-selected: only act if the user hasn't set anything yet.
        if ((row.price && row.price > 0) || row.accountId) return;
      } else {
        // Already auto-selected once: only re-apply if the row still matches
        // what we last applied (the user hasn't touched it since).
        const unchanged =
          prevIntent.kind === "rs"
            ? row.isCore === true && row.price === prevIntent.price && !row.accountId
            : prevIntent.kind === "matrix"
              ? row.accountId === prevIntent.accountId && row.price === prevIntent.price
              : row.accountId === prevIntent.accountId && (row.price ?? 0) === prevIntent.price;
        if (!unchanged) return;

        // Skip if the desired selection hasn't actually changed.
        const sameAsBefore =
          desired.kind === prevIntent.kind &&
          (desired.kind === "rs"
            ? desired.price === (prevIntent as { price: number }).price
            : desired.kind === "matrix"
              ? desired.price === (prevIntent as { price: number; accountId: string }).price &&
              desired.accountId === (prevIntent as { accountId: string }).accountId
              : desired.accountId === (prevIntent as { accountId: string }).accountId &&
              desired.price === (prevIntent as { price: number }).price);
        if (sameAsBefore) return;
      }

      autoSelectedIntentsRef.current[rowId] = desired;

      if (desired.kind === "rs") {
        // RS wins: select it as core/RS and clear any matrix account.
        handleSetTableData(rowId, desired.price, undefined, true, undefined, true);
      } else if (desired.kind === "matrix" && bestOption) {
        handleSetTableData(rowId, bestOption.price, undefined, false, {
          accountId: bestOption.accountId,
          accountName: bestOption.accountName,
          accountType: bestOption.accountType,
          physicalRoutingDefault: bestOption.physicalRoutingDefault,
        });
      } else if (firstRecyclingAccount) {
        handleSetTableData(rowId, 0, undefined, false, {
          accountId: firstRecyclingAccount.accountId,
          accountName: firstRecyclingAccount.accountName,
          accountType: firstRecyclingAccount.accountType,
          physicalRoutingDefault: firstRecyclingAccount.physicalRoutingDefault,
        });
      }
    });
  }, [
    showBestPrice,
    autoSelect,
    vehiclePricingMatrix.isLoading,
    flowTypeAccounts.isLoading,
    tableData,
    bestMatrixPriceByCategoryId,
    firstRecyclingAccount,
    handleSetTableData,
    vehicle?.id,
    autoSelectSuppressedCategoryIds,
  ]);

  const baseCols = useMemo(() => [
    {
      id: 'dropdown',
      cell: ({ row }: any) => {
        const categoryId = row.original.category.id;
        const isExpanded = row.getIsExpanded();

        return (
          <div className="flex justify-center items-center flex-1 p-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}
              style={{ transform: `rotate(${!isExpanded ? "0" : "-270"}deg)` }}
              className="p-1 transition duration-500 cursor-pointer border border-gray-400 rounded-full hover:bg-gray-200"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    colHelper.accessor("category.name", {
      id: "category.name",
      header: "Name",
      cell: (info) => {
        // Read through the ref so this column definition doesn't depend on the conflict map.
        const conflict = categoryConflictsRef.current?.get(info.row.original.category.id);

        return (
          <div className="flex gap-x-2 items-center justify-between">
            <div className="flex items-center gap-x-2">
              <p>{info.getValue()}</p>

            </div>
            <div className="flex items-center gap-x-1">
              {conflict && (
                <span
                  title={conflict.role === "main"
                    ? `Also selected as separate parts: ${conflict.otherNames.join(", ")}`
                    : `Contained within selected: ${conflict.otherNames.join(", ")}`}
                  className="cursor-help rounded-full border border-red-400 bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase leading-tight text-red-800"
                >
                  {conflict.role === "main" ? "Contains selected parts" : "Part of selected main"}
                </span>
              )}
              {info.row.original.isRemoved && (
                <span className="rounded-full border border-yellow-300 bg-yellow-100 px-2 py-0.5 text-[10px] font-semibold uppercase leading-tight text-yellow-800">
                  Added
                </span>
              )}
              {damagedByCategoryIdRef.current?.has(info.row.original.category.id) && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setDamageViewerRef.current({
                      categoryName: info.row.original.category.name,
                      tractableCode: damagedByCategoryIdRef.current.get(info.row.original.category.id)!,
                    });
                  }}
                  title="Damage AI detected damage on this panel — click to view the damage images"
                  className="cursor-pointer rounded-full border border-red-400 bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase leading-tight text-red-800 hover:bg-red-200"
                >
                  Damaged
                </span>
              )}
              <EbayLinkButton query={makeModelString + "+" + info.getValue()} />
            </div>
          </div>
        );
      },
    }),
    colHelper.display({
      id: "lookupMpn",
      header: "MPN",
      cell: (info) => {
        const cat = info.row.original.category;

        const mpnContent = partProLoading ? (
          <LoadingPulse width="w-full" />
        ) : partProUnlocked ? (
          <div className="flex gap-x-2 items-center justify-between">
            <span>{cat.lookupPartNumber ?? "-"}</span>
            {cat.lookupPartNumber && <EbayLinkButton query={cat.lookupPartNumber} />}
          </div>
        ) : (
          <span><LockClosedIcon height={15} width={15} /></span>
        );

        const hasZone = !!cat.ucidZone;
        const hasUcid = !!cat.ucid;
        const showDiagram = !!vehicle?.umc;

        const handleDiagramClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          setDiagramCategory(cat);
          if (hasZone && hasUcid) {
            setDiagramMode({ type: "view", zoneId: cat.ucidZone, ucid: cat.ucid });
          } else if (hasZone) {
            setDiagramMode({ type: "findZone", zoneId: cat.ucidZone });
          } else {
            setDiagramMode({ type: "findAll" });
          }
          setDiagramOpen(true);
        };

        return (
          <div className="space-y-1">
            {mpnContent}
            {showDiagram && (
              <button
                onClick={handleDiagramClick}
                className={classNames(
                  "text-xs px-2 py-0.5 rounded whitespace-nowrap",
                  hasZone && hasUcid
                    ? "bg-primary-100 text-primary-700 hover:bg-primary-200"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                )}
              >
                {hasZone && hasUcid ? "View Diagram" : "Find Diagram"}
              </button>
            )}
          </div>
        );
      }
    }),
    colHelper.accessor(
      row => {
        // Use aggregate field if available, otherwise compute from parts
        if (row.internalStock !== undefined) {
          return row.internalStock;
        }
        return row.parts?.filter(part => !part.soldDate).length ?? 0;
      },
      {
        id: "partsCount",
        header: "Stock",
        cell: (info) => {
          const partsCount = info.getValue() as number;
          const partProCount = 0; //don't have this data yet
          return (
            <div>
              <p>
                <span className="text-gray-600 text-sm">
                  Internal:{" "}
                </span>
                <span>
                  {partsCount > 0 ? partsCount : "-"}
                </span>
              </p>
              <p className="flex items-center gap-x-1">
                <span className="text-gray-600 text-sm">
                  Part Pro:{" "}
                </span>
                {partProLoading ? (
                  <span className="-mr-10">
                    <LoadingPulse width="w-10" />
                  </span>
                ) : partProUnlocked ? (
                  <span className="text-wilma-500 font-semibold">
                    {partProCount > 0 ? partProCount : "-"}
                  </span>
                ) : (
                  <span className=""><LockClosedIcon height={15} width={15} /></span>
                )}
              </p>
            </div>
          );
        }
      }),
    colHelper.accessor(
      row => {
        // Use aggregate field if available, otherwise compute from parts
        if (row.totalSold !== undefined) {
          return row.totalSold;
        }
        return row.parts?.filter(x => x.soldDate)?.length ?? 0;
      },
      {
        id: "soldQuantity",
        header: "Sold",
        cell: (info) => {
          const row = info.row.original;
          // Use aggregate field if available, otherwise compute from parts
          const lastSold = row.lastSoldDate ?? row.parts?.filter(x => x.soldDate)?.sort((a, b) => (a.soldDate! < b.soldDate! ? 1 : -1))[0]?.soldDate;
          // Scrapped parts are soft-deleted, so they only ever arrive on the summary aggregate -
          // there is no `parts` fallback to compute this from. Shown next to the sold count so a
          // category that gets scrapped far more often than it sells is obvious before it is
          // picked for a job.
          const scrapped = row.totalScrapped ?? 0;
          return (
            <div>
              <p>{info.getValue() ?? "-"}</p>
              {scrapped > 0 && (<p className="text-red-700 text-sm">Scrapped: {scrapped}</p>)}
              {lastSold && (<p className="text-gray-600 text-sm">Last Sold: {formatDateTime(lastSold, DateTime.DATE_MED)}</p>)}
            </div>
          )
        }
      }),
    colHelper.accessor("note",
      {
        id: "note",
        header: "Internal Note",
        enableSorting: false,
        cell: (info) => {
          const currentValue = info.getValue();
          const rowId = info.row.original.category.id;
          return (
            <EditableNoteCell
              rowId={rowId}
              value={currentValue ?? ""}
              handleSetTableData={handleSetTableData}
            />
          );
        }
      }),
    colHelper.accessor(
      (row) => {
        // Use aggregate field if available, otherwise compute from parts
        if (row.lastSoldPrice !== undefined && row.lastSoldPrice !== null) {
          return row.lastSoldPrice;
        }
        return row.parts?.find((part: IPart) => part.soldDate)?.price || 0;
      },
      {
        id: "lastSoldPriceButton",
        header: "Last Sold",
        cell: (info) => {
          const row = info.row.original;
          const rowId = row.category.id;

          return (
            <LastSoldPriceButton
              parts={row.parts ?? []}
              onPriceSelect={(price) => handleSetTableData(rowId, price, undefined, undefined, undefined, true)}
              lastSoldPrice={row.lastSoldPrice ?? row.category.lastSoldPrice}
              readOnly={hasTmeFeature}
            />
          );
        },
      }
    ),
    colHelper.accessor(
      (row) => {
        // Use aggregate field if available, otherwise compute from parts
        const basePrice = row.lastSoldPrice ?? (row.parts?.find((part: IPart) => part.soldDate)?.price || 0);
        return parseFloat((basePrice * 1.1).toFixed(2));
      },
      {
        id: "marketPriceButton",
        header: "Market",
        cell: (info) => {
          const row = info.row.original;
          const rowId = row.category.id;

          return (
            <SmartPriceButton
              categoryWithParts={row}
              onPriceSelect={(price) => handleSetTableData(rowId, price, undefined, undefined, undefined, true)}
            />
          );
        },
      }
    ),
    colHelper.accessor((row) => {
      const basePrice = row.category.lookupAveragePrice || 0;
      return parseFloat((basePrice).toFixed(2));
    },
      {
        id: "partProPriceButton",
        header: "PartPro",
        cell: (info) => {
          const row = info.row.original;
          const rowId = row.category.id;

          return (
            <PartProPriceButton
              category={row.category}
              onPriceSelect={(price) => handleSetTableData(rowId, price, undefined, undefined, undefined, true)}
              partProUnlocked={partProUnlocked}
              partProLoading={partProLoading}
            />
          );
        },
      }
    ),
    colHelper.accessor((row) => row.price, {
      id: "price",
      header: "Price",
      sortingFn: "auto",
      // Use our EditablePriceCell component
      cell: (info) => {
        const currentValue = info.getValue() as number;
        const row = info.row.original;
        const rowId = row.category.id;
        return (
          <EditablePriceCell
            rowId={rowId}
            value={currentValue}
            handleSetTableData={handleSetTableData}
          />
        );
      },
    }),
    colHelper.display({
      id: "select",
      header: ({ table }) => (
        <div className="flex justify-center items-center flex-1 gap-x-2 p-1">
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
          <button
            type="button"
            onClick={() => { if (table.getState().rowSelection) table.resetRowSelection(); }}
            className={classNames(
              "p-1 transition duration-500 cursor-pointer border rounded-full",
              table.getSelectedRowModel().rows.length === 0
                ? "pointer-events-none border-gray-300 text-gray-300"
                : "border-primary-700 bg-primary-600 hover:bg-primary-700 text-white"
            )}
            title="Clear selected categories"
          >
            <XMarkSolidIcon className="h-4 w-4" />
          </button>
        </div>
      ),
      cell: ({ row }) => {
        const rowId = row.original.category.id;

        return (
          <div className="flex justify-center items-center flex-1 gap-x-2 p-1" onClick={(e) => e.stopPropagation()}>
            <IndeterminateCheckbox
              {...{
                id: rowId,
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        );
      }
    }),
  ], [handleSetTableData, makeModelString, partProLoading, partProUnlocked, hasTmeFeature]);

  // TME-only column for choosing where a part is physically routed. Defaults from
  // the selected account's PhysicalRoutingDefault but can be overridden per part.
  const routingCol = useMemo(() => [
    colHelper.display({
      id: "physicalRouting",
      header: "Routing",
      cell: ({ row }) => {
        const rowId = row.original.category.id;
        const value = row.original.physicalRouting ?? PhysicalRouting.None;

        return (
          <div className="flex justify-center items-center flex-1 p-1" onClick={(e) => e.stopPropagation()}>
            <select
              value={value}
              onChange={(e) =>
                handleSetTableData(rowId, undefined, undefined, undefined, undefined, false, Number(e.target.value) as PhysicalRouting)
              }
              className="border p-1 text-sm"
            >
              {physicalRoutingOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );
      }
    })
  ], [handleSetTableData]);

  const rsCols = useMemo(() => [
    colHelper.accessor((row) => {
      const basePrice = row.category.lookupAveragePrice || 0;
      return parseFloat((basePrice).toFixed(2));
    },
      {
        id: "rsPriceButton",
        header: "RS",
        cell: (info) => {
          const row = info.row.original;
          const rowId = row.category.id;

          return (
            <RsPriceButton
              category={row.category}
              onPriceSelect={(price) => handleSetTableData(rowId, price, undefined, true, undefined, true)}
            />
          );
        },
      }
    )
  ], [handleSetTableData]);

  useEffect(() => {
    if (singleSetting.data?.data && singleSetting.data.data.value != "" && singleSetting.data.data.value != null) {
      setHasRsKey(true);
    } else {
      setHasRsKey(false);
    }
  }, [singleSetting.data, singleSetting.isLoading]);

  const accountCol = useMemo(() => [
    colHelper.display({
      id: "accountPriceButton",
      header: "Flow",
      cell: (info) => {
        const row = info.row.original;
        const rowId = row.category.id;
        const pricingData = bestMatrixPriceByCategoryId.get(rowId);
        const bestPricing = pricingData?.bestOption;
        const rsPrice = row.category.rsPrice ?? null;

        // Prefer the row's currently selected flow over the matrix "best" price,
        // so changing Flow Options updates what the cell shows.
        const selectedIsRs = row.isCore === true && !row.accountId;
        const selectedIsRecycling = !!row.accountId && (
          row.accountType === 8
          || (!!firstRecyclingAccount && row.accountId === firstRecyclingAccount.accountId)
        );
        const selectedMatrixOption = row.accountId && !selectedIsRecycling
          ? pricingData?.options.find((option) => option.accountId === row.accountId) ?? null
          : null;
        const nothingSelected = !row.accountId && !row.isCore;

        // Fall back to best when nothing has been selected yet.
        const showRs = selectedIsRs
          || (nothingSelected && hasTmeFeature && rsPrice != null && rsPrice > (bestPricing?.price ?? 0));
        const visibleMatrixOption = selectedMatrixOption
          ?? (nothingSelected ? bestPricing ?? null : null);

        const accountLabelName = selectedIsRecycling
          ? (row.accountName ?? firstRecyclingAccount?.accountName ?? "Recycling")
          : selectedMatrixOption?.accountName
            ?? (showRs ? null : visibleMatrixOption?.accountName ?? null);
        const accountLabelType = selectedIsRecycling
          ? 8
          : selectedMatrixOption?.accountType
            ?? (showRs ? null : visibleMatrixOption?.accountType ?? null);

        const hasVisibleFlow = showRs || !!visibleMatrixOption || selectedIsRecycling;

        const flowTag = hasVisibleFlow && (showRs ? (
          <span
            className="shrink-0 whitespace-nowrap rounded-sm border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10px] font-normal leading-tight text-[#69141E]"
            title="RS Automotive Price"
          >
            RS Automotive
          </span>
        ) : selectedIsRecycling ? (
          <span
            className={classNames(
              "shrink-0 whitespace-nowrap rounded-sm border px-1.5 py-0.5 text-[10px] font-normal leading-tight",
              accountTypePillClassMap[8]
            )}
            title={accountLabelName ?? "Recycling"}
          >
            {accountLabelName ?? "Recycling"}
          </span>
        ) : accountLabelName ? (
          <span
            className={classNames(
              "shrink-0 whitespace-nowrap rounded-sm border px-1.5 py-0.5 text-[10px] font-normal leading-tight",
              typeof accountLabelType === "number"
                ? accountTypePillClassMap[accountLabelType] ?? "border-gray-200 bg-gray-100 text-gray-500"
                : "border-gray-200 bg-gray-100 text-gray-500"
            )}
            title={accountLabelName}
          >
            {accountLabelName}
          </span>
        ) : null);

        return (
          <div className="flex items-center gap-x-1.5 whitespace-nowrap">
            {showRs ? (
              <RsPriceButton
                category={row.category}
                onPriceSelect={(price) => handleSetTableData(rowId, price, undefined, true, undefined, true)}
              />
            ) : visibleMatrixOption ? (
              <button
                type="button"
                onClick={() =>
                  handleSetTableData(rowId, visibleMatrixOption.price, undefined, false, {
                    accountId: visibleMatrixOption.accountId,
                    accountName: visibleMatrixOption.accountName,
                    accountType: visibleMatrixOption.accountType,
                    physicalRoutingDefault: visibleMatrixOption.physicalRoutingDefault,
                  })
                }
                className="border text-white shadow rounded-md p-1 px-2 bg-emerald-600 hover:bg-emerald-500 min-w-[5rem] text-center"
              >
                <span className="font-bold">{PriceFormat(visibleMatrixOption.price)}</span>
              </button>
            ) : null}
            {flowTag}
            {/* Always available: opens flow options (other account prices + Recycling). */}
            <button
              type="button"
              onClick={() => handleOpenPriceOptions(row)}
              className="shrink-0 rounded-md border border-gray-300 bg-white p-1 text-gray-600 hover:bg-gray-50"
              title="Flow options"
            >
              <EllipsisVerticalIcon className="h-4 w-4" />
            </button>
          </div>
        );
      },
    })
  ], [bestMatrixPriceByCategoryId, firstRecyclingAccount, handleOpenPriceOptions, handleSetTableData, hasTmeFeature]);

  // slice rsCols/best column into base columns after source price columns
  const cols = useMemo(() => {
    let newCols = [...baseCols];

    // TME hides the Market and PartPro source-price columns.
    if (hasTmeFeature) {
      newCols = newCols.filter(col => col.id !== "marketPriceButton" && col.id !== "partProPriceButton");
    }

    // RS column is also hidden under TME.
    if (hasRsKey && !hasTmeFeature) {
      const partProIndex = newCols.findIndex(col => col.id === "partProPriceButton");
      if (partProIndex !== -1) {
        newCols.splice(partProIndex + 1, 0, ...rsCols);
      }
    }

    if (showBestPrice) {
      const priceIndex = newCols.findIndex(col => col.id === "price");
      if (priceIndex !== -1) {
        newCols.splice(priceIndex, 0, ...accountCol);
      }
    }

    // TME-only Routing dropdown, placed right after the Internal Note column.
    if (hasTmeFeature) {
      const noteIndex = newCols.findIndex(col => col.id === "note");
      if (noteIndex !== -1) {
        newCols.splice(noteIndex + 1, 0, ...routingCol);
      } else {
        newCols.push(...routingCol);
      }
    }

    return newCols;
  }, [hasRsKey, baseCols, rsCols, showBestPrice, accountCol, routingCol, hasTmeFeature]);


  const table = useReactTable({
    data: filteredTableData,
    columns: cols,
    getRowId: (row) => row.category.id,
    state: {
      sorting: sorting,
      rowSelection: rowSelection,
      expanded: expanded
    },
    enableRowSelection: true,
    onRowSelectionChange: (updaterOrState) => {
      // If it's a function, call it with the current state to get newState.
      const newState: Record<string, boolean> =
        typeof updaterOrState === "function"
          ? updaterOrState(rowSelection)
          : updaterOrState;

      const newlySelected: CategoryWithParts[] = [];
      for (const [rowId, isSel] of Object.entries(newState)) {
        if (isSel) {
          const found = tableData.find((c) => c.category.id === rowId);
          if (found) newlySelected.push(found);
        }
      }
      setSelectedCategoriesRef.current(newlySelected);
    },
    onExpandedChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        setExpanded(prev => {
          const newValue = updaterOrValue(prev);
          // Ensure only one row is expanded at a time
          if (typeof newValue === 'boolean') return {};
          const trueKeys = Object.keys(newValue).filter(key => newValue[key]);
          if (trueKeys.length > 1) {
            // Keep only the last one (most recently expanded)
            const lastKey = trueKeys[trueKeys.length - 1];
            return { [lastKey]: true };
          }
          return newValue;
        });
      } else {
        if (typeof updaterOrValue === 'boolean') {
          setExpanded({});
        } else {
          // Ensure only one row is expanded
          const trueKeys = Object.keys(updaterOrValue).filter(key => updaterOrValue[key]);
          if (trueKeys.length > 1) {
            const lastKey = trueKeys[trueKeys.length - 1];
            setExpanded({ [lastKey]: true });
          } else {
            setExpanded(updaterOrValue);
          }
        }
      }
    },
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    setTableData(prev => {
      const prevById = new Map(prev.map(c => [c.category.id, c]));

      return categories.map(incoming => {
        const local = prevById.get(incoming.category.id);
        if (!local) return incoming;

        return {
          ...incoming,
          // Preserve user-entered local fields
          ...(local.price != null ? { price: local.price } : {}),
          ...(local.note != null ? { note: local.note } : {}),
          ...(local.isCore != null ? { isCore: local.isCore } : {}),
          ...(local.accountId !== undefined ? { accountId: local.accountId } : {}),
          ...(local.accountName !== undefined ? { accountName: local.accountName } : {}),
          ...(local.accountType !== undefined ? { accountType: local.accountType } : {}),
          ...(local.physicalRouting !== undefined ? { physicalRouting: local.physicalRouting } : {}),
          category: {
            ...incoming.category,
          },
        };
      });
    });
  }, [categories]);

  return (
    <>
      <Modal open={priceOptionsModalOpen} setOpen={setPriceOptionsModalOpen} width="max-w-xl">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Flow Options</h3>
            <p className="text-sm text-gray-600">
              {selectedPriceOptions?.categoryName ?? ""}
            </p>
          </div>

          {!!selectedPriceOptions && (
            <div className="space-y-3">
              {selectedPriceOptions.bestOption && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Best Price</div>
                  <button
                    type="button"
                    onClick={() => handleSelectModalPrice(selectedPriceOptions.bestOption!)}
                    className="w-full flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 hover:bg-emerald-100"
                  >
                    <span className="font-semibold text-emerald-700">{PriceFormat(selectedPriceOptions.bestOption.price)}</span>
                    <span
                      className={classNames(
                        "truncate rounded-sm border px-1.5 py-0.5 text-[10px] font-normal leading-tight",
                        accountTypePillClassMap[selectedPriceOptions.bestOption.accountType] ?? "border-gray-200 bg-gray-100 text-gray-500"
                      )}
                    >
                      {selectedPriceOptions.bestOption.accountName} ({accountTypeLabelMap[selectedPriceOptions.bestOption.accountType] ?? `Type ${selectedPriceOptions.bestOption.accountType}`})
                    </span>
                  </button>
                </div>
              )}

              {selectedPriceOptions.rsPrice != null && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">RS Automotive</div>
                  <button
                    type="button"
                    onClick={handleSelectModalRs}
                    className="w-full flex items-center justify-between rounded-md border border-rose-200 bg-rose-50 px-3 py-2 hover:bg-rose-100"
                  >
                    <span className="font-semibold text-[#69141E]">{PriceFormat(selectedPriceOptions.rsPrice)}</span>
                    <span className="truncate rounded-sm border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10px] font-normal leading-tight text-[#69141E]">
                      RS Automotive (Core)
                    </span>
                  </button>
                </div>
              )}

              {selectedPriceOptions.otherOptions.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Other Prices</div>
                  <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-200">
                    {selectedPriceOptions.otherOptions.map((option) => (
                      <button
                        key={`${option.accountId}-${option.price}`}
                        type="button"
                        onClick={() => handleSelectModalPrice(option)}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50"
                      >
                        <span className="font-medium text-gray-800">{PriceFormat(option.price)}</span>
                        <span
                          className={classNames(
                            "truncate rounded-sm border px-1.5 py-0.5 text-[10px] font-normal leading-tight",
                            accountTypePillClassMap[option.accountType] ?? "border-gray-200 bg-gray-100 text-gray-500"
                          )}
                        >
                          {option.accountName} ({accountTypeLabelMap[option.accountType] ?? `Type ${option.accountType}`})
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {firstRecyclingAccount && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Recycling</div>
                  <button
                    type="button"
                    onClick={handleSelectModalRecycling}
                    className="w-full flex items-center justify-between rounded-md border border-purple-200 bg-purple-50 px-3 py-2 hover:bg-purple-100"
                  >
                    <span className="font-semibold text-purple-700">Recycling</span>
                    <span
                      className={classNames(
                        "truncate rounded-sm border px-1.5 py-0.5 text-[10px] font-normal leading-tight",
                        accountTypePillClassMap[firstRecyclingAccount.accountType] ?? "border-gray-200 bg-gray-100 text-gray-500"
                      )}
                    >
                      {firstRecyclingAccount.accountName} ({accountTypeLabelMap[firstRecyclingAccount.accountType] ?? `Type ${firstRecyclingAccount.accountType}`})
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      <div className="mt-4 bg-white overflow-auto space-y-4">
        <table className="min-w-full overflow-scroll divide-y border divide-gray-300">

          {/* Table headings - using nth child to make the name column the same width */}
          {/* Also using first and last to make sure the dropdown and select boxes are the same width */}
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary-50 text-gray-800 [&>*:nth-child(2)]:w-48" >
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="flex first:w-16 last:w-16 py-1 px-3 text-sm font-semibold lg:table-cell text-left"
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>

                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {/* Body */}
          <tbody>
            {table.getRowModel().rows.map((row) => {
              let rowCells = row.getVisibleCells();
              let isRemoved = row.original.isRemoved === true;
              let noPriceSelected = row.getIsSelected() === true && (!row.original.price || row.original.price <= 0) && !row.original.accountId;
              // Deliberately the global map rather than row.getIsSelected(): the clashing
              // category is usually selected in a different table.
              const conflict = categoryConflicts?.get(row.original.category.id);

              return (
                <Fragment key={row.id}>
                  <tr
                    key={row.id}
                    // onClick={() => setLocation(row.original)}
                    title={conflict
                      ? conflict.role === "main"
                        ? `Also selected as separate parts: ${conflict.otherNames.join(", ")}`
                        : `Contained within selected: ${conflict.otherNames.join(", ")}`
                      : undefined}
                    // Conflict wins over isRemoved/noPriceSelected - it's the only one the
                    // operator is being asked to act on. A darker red than the no-price
                    // bg-red-200 so the two stay distinguishable.
                    className={classNames(conflict ? "bg-red-300" : isRemoved ? "bg-yellow-100" : noPriceSelected ? "bg-red-200" : "", `hover:bg-gray-100`)}
                  >
                    {rowCells.map((cell) => (
                      <td
                        key={cell.id}
                        className={classNames(
                          "px-3 border text-sm",
                          cell.column.id === "category.name" ? "w-72" : "",
                          cell.column.id === "accountPriceButton" ? "whitespace-nowrap" : ""
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <tr>
                      {/* 2nd row is a custom 1 cell row */}
                      <td colSpan={row.getVisibleCells().length}>
                        <LeadPartsCategoriesTableSubRow
                          categoryWithParts={row.original}
                          vehicleId={vehicle.id}
                          vehicleYear={vehicle.dvlaYearOfManufacture ?? null}
                          vehicleVisibilityDate={vehicle.visibilityDate ?? null}
                          vehicleTerminateDate={vehicle.terminateDate ?? null}
                          vehicleModelSeries={vehicle.modelSeries ?? null}
                          leadPartsTableFilters={leadPartsTableFilters}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>

        </table>
      </div>

      {vehicle?.umc && diagramCategory && (
        <PartDiagramModal
          open={diagramOpen}
          setOpen={setDiagramOpen}
          umc={vehicle.umc}
          category={diagramCategory}
          mode={diagramMode}
        />
      )}

      <DamageImagesModal
        open={damageViewer !== null}
        setOpen={(value) => {
          const next = typeof value === "function" ? value(damageViewer !== null) : value;
          if (!next) setDamageViewer(null);
        }}
        categoryName={damageViewer?.categoryName ?? null}
        images={damageViewerImages}
      />
    </>
  );
}
